<template>
  <div>
    <input
      :id="inputId"
      :name="name"
      :aria-describedby="ariaDescribedByName"
      type="checkbox"
      :checked="shouldBeChecked"
      :value="value"
      :class="checkboxClasses"
      :disabled="isDisabled"
      :tabindex="tabIndex"
      :required="required"
      @change="updateInput"
      @blur="$emit('blur')"
      @click="removeMouseFocus"
    />
    <label :class="checkboxLabelClasses" :for="inputId" :aria-label="ariaLabel">
      {{ labelName }}
      <span v-if="formattedLabelText" v-html="formattedLabelText"></span>
    </label>
    <HelperText v-if="helperText" :id="inputId" :text="helperText" :status="status" />
  </div>
</template>

<script>
import helperStatus from '../../helpers/status';
import HelperText from '../helper_text/HelperText.vue';

export default {
  name: 'Checkbox',
  components: {
    HelperText,
  },
  model: {
    prop: 'modelValue',
    event: 'change',
  },
  props: {
    ariaLabel: {
      type: String,
      default: '',
      required: false,
    },
    value: {
      type: [String, Number],
      default: null,
    },
    modelValue: {
      type: [Array, Boolean, String],
      default: false,
    },
    labelName: {
      type: String,
      default: '',
      required: false,
    },
    formattedLabelText: {
      type: String,
      default: null,
    },
    trueValue: {
      type: [Boolean, String, Number],
      default: true,
    },
    falseValue: {
      type: [Boolean, String, Number],
      default: false,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    checkboxId: {
      type: String,
      default: '',
    },
    labelClass: {
      type: String,
      default: 'mb-24',
    },
    status: {
      type: Number,
      default: helperStatus.Neutral,
    },
    helperText: {
      type: String,
      default: '',
      required: false,
    },
    name: {
      type: String,
      default: '',
      required: false,
    },
    tabIndex: {
      type: Number,
      default: null,
      required: false,
    },
    preventDefaultChange: {
      type: Boolean,
      default: false,
      required: false,
    },
    required: {
      type: Boolean,
      required: false,
    },
  },
  computed: {
    inputId() {
      return this.checkboxId ? this.checkboxId : `checkbox-${this.value}`;
    },
    shouldBeChecked() {
      if (this.modelValue instanceof Array) {
        return this.modelValue.includes(this.value);
      }
      // Note that `true-value` and `false-value` are camelCase in the JS
      return this.modelValue === this.trueValue;
    },
    checkboxLabelClasses() {
      // disabled functionality for label is controlled
      // by setting the checkbox class to checkbox-disable
      return `checkbox-label ${this.labelClass}`;
    },
    checkboxClasses() {
      return this.isDisabled ? 'checkbox-disabled' : 'checkbox';
    },
    hasError() {
      return this.status === helperStatus.Error;
    },
    ariaDescribedByName() {
      if (!this.helperText) {
        return null;
      }
      return `${this.inputId}-helper`;
    },
  },
  methods: {
    removeMouseFocus(event) {
      // Only remove focus when using mouse
      // screenX and screenY value is 0 for Spacebar
      if (event.screenX + event.screenY !== 0) {
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }
    },
    updateInput(event) {
      const isChecked = event.target.checked;
      if (this.preventDefaultChange) {
        this.$emit('click');
      } else if (this.modelValue instanceof Array) {
        const newValue = [...this.modelValue];

        if (isChecked) {
          newValue.push(this.value);
        } else {
          newValue.splice(newValue.indexOf(this.value), 1);
        }

        this.$emit('change', newValue);
      } else {
        this.$emit('change', isChecked ? this.trueValue : this.falseValue);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.checkbox {
  opacity: 0;
  position: absolute;

  &:focus-visible + .checkbox-label {
    border-radius: theme('borderRadius.md');
    outline: 2px dotted theme('colors.primary.DEFAULT');
    outline-offset: 2px;
  }
  + .checkbox-label {
    color: theme('colors.grey.body');
    cursor: pointer;
    position: relative;
    line-height: 1.5rem;
    display: inline-block;
    padding-left: 2rem;
    &::before {
      content: '';
      background: theme('colors.foreground');
      border: 1px solid theme('colors.grey.clickable');
      display: inline-block;
      height: 1.5rem;
      width: 1.5rem;
      transition: all 250ms ease;
      vertical-align: middle;
      position: absolute;
      top: 0;
      left: 0;
      margin-right: 0.5rem;
      border-radius: theme('borderRadius.md');
    }
  }

  &:checked {
    + .checkbox-label {
      &::before {
        background: theme('colors.primary.DEFAULT');
        border: 1px solid theme('colors.primary.DEFAULT');
      }
      &::after {
        content: '';
        border: solid theme('colors.foreground');
        border-width: 0 2px 2px 0;
        display: inline-block;
        transform: rotate(45deg);
        left: 8px;
        top: 2px;
        width: 8px;
        height: 16px;
        position: absolute;
      }
    }
  }
}
.checkbox-disabled {
  opacity: 0;
  position: absolute;
  + .checkbox-label {
    cursor: not-allowed;
    position: relative;
    line-height: 1.5rem;
    display: inline-block;
    padding-left: 2rem;
    color: theme('colors.grey.text.subdued');
    &::before {
      content: '';
      background: theme('colors.grey.divider.subdued');
      border: 1px solid theme('colors.grey.graphics.subdued');
      display: inline-block;
      height: 1.5rem;
      width: 1.5rem;
      transition: all 250ms ease;
      vertical-align: middle;
      position: absolute;
      top: 0;
      left: 0;
      margin-right: 0.5rem;
      border-radius: theme('borderRadius.md');
    }
  }
  &:checked {
    + .checkbox-label {
      &::before {
        background: theme('colors.grey.divider.subdued');
        border: 1px solid theme('colors.grey.divider.subdued');
      }
      &::after {
        content: '';
        border: solid theme('colors.grey.graphics.subdued');
        border-width: 0 2px 2px 0;
        display: inline-block;
        transform: rotate(45deg);
        left: 8px;
        top: 2px;
        width: 8px;
        height: 16px;
        position: absolute;
      }
    }
  }
}
</style>
